import { FormControl, InputLabel, Select, SelectProps } from '@mui/material'
import React, { PropsWithChildren, useMemo } from 'react'
import randomstring from 'randomstring';
import BTThemeProvider from '@common-components/bt-theme-provider';
import { usePortalContainer } from '@/common/hooks/use-portal-container';

type BTDropDownProps = {
    fullWidth?: boolean;
}

export default function BTDropDown({ label, children, sx, fullWidth = true, MenuProps, ...props }: PropsWithChildren<BTDropDownProps> & SelectProps) {
    const labelId = useMemo(() => randomstring.generate(16), []);
    
    // Get the portal container for Shadow DOM support
    const portalContainer = usePortalContainer();
    
    // Merge MenuProps to include the portal container for Shadow DOM
    // MUI Select's Menu extends Popover, so container should be set directly on MenuProps
    // Note: MUI's container prop only accepts Element, not ShadowRoot, so we filter that out
    const mergedMenuProps = useMemo(() => {
        // Only use HTMLElement, not ShadowRoot (MUI doesn't accept ShadowRoot)
        const containerElement = portalContainer instanceof HTMLElement
            ? portalContainer
            : null;
        const isShadowDOM = containerElement && containerElement !== document.body;

        if (!isShadowDOM) {
            return MenuProps;
        }

        // CRITICAL: The portal container has pointer-events: none
        // We need to ensure all Menu components are interactive
        const shadowDOMMenuProps: Partial<typeof MenuProps> = {
            ...MenuProps,
            container: containerElement,
            PaperProps: {
                ...MenuProps?.PaperProps,
                style: {
                    pointerEvents: 'auto' as const,
                    ...(MenuProps?.PaperProps?.style || {}),
                },
            },
            BackdropProps: {
                ...MenuProps?.BackdropProps,
                style: {
                    pointerEvents: 'auto' as const,
                    ...(MenuProps?.BackdropProps?.style || {}),
                },
            },
        };

        return shadowDOMMenuProps;
    }, [MenuProps, portalContainer]);
    
    return ( 
        <BTThemeProvider>
            <FormControl fullWidth={fullWidth}>
                {label && <InputLabel
                    sx={{
                        '&.MuiInputLabel-root': {
                            color: 'var(--tasks-text-secondary)'
                        },
                    }}
                    id={labelId}>{label}</InputLabel>}
                <Select
                    labelId={labelId}
                    label={label}
                    MenuProps={mergedMenuProps}
                    sx={{
                        '&:hover:not(.Mui-disabled):before': {
                            borderColor: '210 40% 96.1%',
                        },
                        '&:before': {
                            borderColor: '210 40% 96.1%',
                        },
                        '.MuiSelect-select': {
                            color: 'var(--tasks-text-secondary)',
                        },
                        '.MuiSvgIcon-root': {
                            color: 'var(--tasks-text-secondary)',
                        },
                        '.MuiOutlinedInput-notchedOutline': {
                            borderColor: '210 40% 96.1%',
                        },
                        '.MuiOutlinedInput-input': {
                            '&.Mui-disabled': {
                                color: 'var(--tasks-text-secondary)',
                                WebkitTextFillColor: 'var(--tasks-text-secondary)',
                            }
                        },
                        '&.Mui-disabled': {
                            color: 'var(--tasks-text-secondary)',

                            '&.MuiSelect-select': {
                                color: 'var(--tasks-text-secondary)',
                            },

                            '.MuiSvgIcon-root': {
                                color: 'var(--tasks-text-secondary)',
                            },
                            '.MuiOutlinedInput-notchedOutline': {
                                borderColor: '210 40% 96.1%',
                            },
                        },
                        ...sx
                    }}
                    {...props}
                >
                    {children}
                </Select>
            </FormControl>
        </BTThemeProvider>
    )
}